/**
 * MEETINGS PAGE - FLO SIGNATURE DESIGN SYSTEM v2
 * Clean. Controlled. Consistent. Light ops mode.
 */

import React, { useState, useEffect } from 'react';
import { apiClient } from '../api/apiConfig';
import {
  CalendarIcon,
  UserGroupIcon,
  XMarkIcon,
  PlayIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  LinkIcon,
  TrashIcon,
  FolderIcon
} from '@heroicons/react/24/outline';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import { Panel, Button, Badge, Input, Screen } from '../design/primitives';
import { Alert, Spinner } from '../components/ui';

interface Business {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
}

interface Category {
  id: number;
  name: string;
}

interface Project {
  id: number;
  name: string;
  status: string;
}

interface CalendarEvent {
  id: number;
  title: string;
  start: string;
  end: string;
  description: string;
  is_meeting: boolean;
  event_type: string;
  business?: Business;
  category?: Category;
  participants: number[];
  project?: Project;
}

interface Person {
  id: number;
  name: string;
}

interface Recording {
  id: number;
  video_id: string;
  blue_dot_meeting_id: string;
  title: string;
  event_type: string;
  summary?: string;
  summary_v2?: string;
  transcript?: string;
  duration?: number;
  attendees: string[];
  created_at: string;
  updated_at: string;
  processed_at?: string;
  calendar_event?: CalendarEvent;
  has_summary: boolean;
  has_transcript: boolean;
  is_complete: boolean;
  transcript_text?: string;
  project?: Project;
  business?: Business;
  categories?: Category[];
  attendee_people?: Person[];
}

const MeetingsPage: React.FC = () => {
  const [meetings, setMeetings] = useState<CalendarEvent[]>([]);
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTab, setSelectedTab] = useState<'meetings' | 'recordings'>('meetings');
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [linkingRecording, setLinkingRecording] = useState<number | null>(null);
  const [editingProject, setEditingProject] = useState<{type: 'meeting' | 'recording', id: number} | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [meetingsRes, recordingsRes, projectsRes, businessesRes, categoriesRes, peopleRes] = await Promise.all([
        apiClient.get('/api/calendar_events/?is_meeting=true'),
        apiClient.get('/api/recordings/'),
        apiClient.get('/api/projects/'),
        apiClient.get('/api/businesses/'),
        apiClient.get('/api/categories/'),
        apiClient.get('/api/people/'),
      ]);

      setMeetings(meetingsRes.data);
      setRecordings(recordingsRes.data);
      setProjects(projectsRes.data);
      setBusinesses(businessesRes.data);
      setCategories(categoriesRes.data);
      setPeople(peopleRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load meetings data');
    } finally {
      setLoading(false);
    }
  };

  const linkRecordingToMeeting = async (recordingId: number, meetingId: number) => {
    try {
      await apiClient.patch(`/api/recordings/${recordingId}/`, { calendar_event: meetingId });
      await fetchData();
      setLinkingRecording(null);
    } catch (err) {
      console.error('Error linking recording to meeting:', err);
      setError('Failed to link recording to meeting');
    }
  };

  const createMeetingFromRecording = async (recording: Recording) => {
    try {
      const meetingData = {
        title: recording.title || 'Blue Dot Meeting',
        start: recording.created_at,
        end: recording.created_at,
        description: recording.summary_v2 || recording.summary || '',
        is_meeting: true,
        event_type: 'meeting'
      };

      const response = await apiClient.post('/api/calendar_events/', meetingData);
      await apiClient.patch(`/api/recordings/${recording.id}/`, { calendar_event: response.data.id });
      await fetchData();
    } catch (err) {
      console.error('Error creating meeting from recording:', err);
      setError('Failed to create meeting from recording');
    }
  };

  const deleteMeeting = async (meetingId: number) => {
    if (!window.confirm('Are you sure you want to delete this meeting?')) return;

    try {
      await apiClient.delete(`/api/calendar_events/${meetingId}/`);
      await fetchData();
    } catch (err) {
      console.error('Error deleting meeting:', err);
      setError('Failed to delete meeting');
    }
  };

  const deleteRecording = async (recordingId: number) => {
    if (!window.confirm('Are you sure you want to delete this recording?')) return;

    try {
      await apiClient.delete(`/api/recordings/${recordingId}/`);
      await fetchData();
    } catch (err) {
      console.error('Error deleting recording:', err);
      setError('Failed to delete recording');
    }
  };

  const updateProject = async (type: 'meeting' | 'recording', itemId: number, projectId: number | null) => {
    try {
      if (type === 'meeting') {
        await apiClient.patch(`/api/calendar_events/${itemId}/`, { project_write: projectId });
      } else {
        await apiClient.patch(`/api/recordings/${itemId}/`, { project_write: projectId });
      }
      await fetchData();
      setEditingProject(null);
    } catch (err) {
      console.error('Error updating project:', err);
      setError('Failed to update project');
    }
  };

  const updateRecordingAssignments = async (recordingId: number, data: {
    business_write?: number | null;
    category_ids?: number[];
    attendee_person_ids?: number[];
  }) => {
    try {
      await apiClient.patch(`/api/recordings/${recordingId}/`, data);
      await fetchData();
      // Don't close editing - allow multiple updates
    } catch (err) {
      console.error('Error updating recording assignments:', err);
      setError('Failed to update assignments');
    }
  };

  const togglePersonAttendee = async (recordingId: number, personId: number, currentAttendees: Person[]) => {
    const currentIds = currentAttendees.map(p => p.id);
    const newIds = currentIds.includes(personId)
      ? currentIds.filter(id => id !== personId)
      : [...currentIds, personId];

    await updateRecordingAssignments(recordingId, { attendee_person_ids: newIds });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'Unknown duration';
    const minutes = Math.round(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const toggleExpanded = (itemType: string, itemId: number) => {
    const key = `${itemType}-${itemId}`;
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const isExpanded = (itemType: string, itemId: number) => {
    return expandedItems.has(`${itemType}-${itemId}`);
  };

  const filteredMeetings = meetings.filter(meeting =>
    meeting.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    meeting.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredRecordings = recordings.filter(recording =>
    recording.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (recording.summary && recording.summary.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) {
    return (
      <Screen>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh'
        }}>
          <div style={{ textAlign: 'center' }}>
            <Spinner size="lg" />
            <p style={{
              marginTop: SPACING[16],
              fontFamily: TYPOGRAPHY.body.fontFamily,
              fontSize: TYPOGRAPHY.body.sizes.default,
              color: COLORS.text.secondary
            }}>
              Loading meetings...
            </p>
          </div>
        </div>
      </Screen>
    );
  }

  return (
    <Screen>
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[24] }}>
      {/* Header */}
      <Panel>
        <h1 style={{
          fontFamily: TYPOGRAPHY.headings.fontFamily,
          fontSize: TYPOGRAPHY.headings.sizes.h1,
          fontWeight: TYPOGRAPHY.headings.weight,
          color: COLORS.text.primary,
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: SPACING[12]
        }}>
          <UserGroupIcon style={{ width: '32px', height: '32px', color: COLORS.accent.default }} />
          Meetings & Recordings
        </h1>
        <p style={{
          marginTop: SPACING[8],
          fontFamily: TYPOGRAPHY.body.fontFamily,
          fontSize: TYPOGRAPHY.body.sizes.default,
          color: COLORS.text.secondary,
          margin: `${SPACING[8]} 0 0 0`
        }}>
          View calendar meetings and link them to Blue Dot recordings.
        </p>

        {error && (
          <div style={{ marginTop: SPACING[16] }}>
            <Alert variant="error">{error}</Alert>
          </div>
        )}
      </Panel>

      {/* Search and Tabs */}
      <Panel>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: SPACING[16]
        }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: '448px' }}>
            <div style={{
              position: 'absolute',
              left: SPACING[12],
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center'
            }}>
              <MagnifyingGlassIcon style={{ height: '20px', width: '20px', color: COLORS.text.tertiary }} />
            </div>
            <input
              type="text"
              placeholder="Search meetings and recordings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                paddingLeft: SPACING[40],
                paddingRight: SPACING[16],
                paddingTop: SPACING[8],
                paddingBottom: SPACING[8],
                width: '100%',
                border: `1px solid ${COLORS.border.default}`,
                borderRadius: RADIUS,
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.default,
                color: COLORS.text.primary,
                backgroundColor: COLORS.bg.panel,
                outline: 'none',
                transition: 'border-color 0.1s ease'
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = COLORS.accent.default)}
              onBlur={(e) => (e.currentTarget.style.borderColor = COLORS.border.default)}
            />
          </div>

          <div style={{
            display: 'flex',
            gap: SPACING[4],
            backgroundColor: COLORS.bg.base,
            padding: SPACING[4],
            borderRadius: RADIUS
          }}>
            <button
              onClick={() => setSelectedTab('meetings')}
              style={{
                padding: `${SPACING[8]} ${SPACING[16]}`,
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.default,
                fontWeight: '500',
                borderRadius: RADIUS,
                transition: 'all 0.1s ease',
                backgroundColor: selectedTab === 'meetings' ? COLORS.bg.panel : 'transparent',
                color: selectedTab === 'meetings' ? COLORS.accent.default : COLORS.text.secondary,
                border: 'none',
                cursor: 'pointer'
              }}
            >
              Calendar Meetings ({filteredMeetings.length})
            </button>
            <button
              onClick={() => setSelectedTab('recordings')}
              style={{
                padding: `${SPACING[8]} ${SPACING[16]}`,
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.default,
                fontWeight: '500',
                borderRadius: RADIUS,
                transition: 'all 0.1s ease',
                backgroundColor: selectedTab === 'recordings' ? COLORS.bg.panel : 'transparent',
                color: selectedTab === 'recordings' ? COLORS.accent.default : COLORS.text.secondary,
                border: 'none',
                cursor: 'pointer'
              }}
            >
              Blue Dot Recordings ({filteredRecordings.length})
            </button>
          </div>
        </div>
      </Panel>

      {/* Meetings Tab */}
      {selectedTab === 'meetings' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
          {filteredMeetings.length === 0 ? (
            <Panel>
              <div style={{ padding: SPACING[48], textAlign: 'center' }}>
                <CalendarIcon style={{
                  width: '48px',
                  height: '48px',
                  color: COLORS.text.tertiary,
                  margin: '0 auto',
                  marginBottom: SPACING[16]
                }} />
                <p style={{
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.headings.sizes.h3,
                  color: COLORS.text.secondary,
                  margin: 0,
                  marginBottom: SPACING[8]
                }}>No meetings found</p>
                <p style={{
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  color: COLORS.text.tertiary,
                  margin: 0
                }}>
                  {searchQuery ? 'Try adjusting your search criteria' : 'Create meetings in your calendar with is_meeting=true'}
                </p>
              </div>
            </Panel>
          ) : (
            filteredMeetings.map((meeting) => {
              const linkedRecording = recordings.find(r => r.calendar_event?.id === meeting.id);

              return (
                <Panel key={meeting.id}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <h3 style={{
                        fontFamily: TYPOGRAPHY.headings.fontFamily,
                        fontSize: TYPOGRAPHY.headings.sizes.h3,
                        fontWeight: TYPOGRAPHY.headings.weight,
                        color: COLORS.text.primary,
                        margin: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: SPACING[8]
                      }}>
                        <CalendarIcon style={{ width: '20px', height: '20px', color: COLORS.accent.default }} />
                        {meeting.title}
                      </h3>
                      <button
                        onClick={() => deleteMeeting(meeting.id)}
                        title="Delete meeting"
                        style={{
                          color: COLORS.status.error,
                          padding: SPACING[8],
                          backgroundColor: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          borderRadius: RADIUS,
                          transition: 'background-color 0.1s ease'
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                      >
                        <TrashIcon style={{ width: '20px', height: '20px' }} />
                      </button>
                    </div>

                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: SPACING[16],
                      fontFamily: TYPOGRAPHY.body.fontFamily,
                      fontSize: TYPOGRAPHY.body.sizes.small,
                      color: COLORS.text.secondary
                    }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: SPACING[4] }}>
                        <ClockIcon style={{ width: '16px', height: '16px' }} />
                        {formatDate(meeting.start)}
                      </span>
                    </div>

                    {/* Project Assignment */}
                    <div>
                      {editingProject?.type === 'meeting' && editingProject?.id === meeting.id ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                          <select
                            value={meeting.project?.id || ''}
                            onChange={(e) => updateProject('meeting', meeting.id, e.target.value ? Number(e.target.value) : null)}
                            style={{
                              fontFamily: TYPOGRAPHY.body.fontFamily,
                              fontSize: TYPOGRAPHY.body.sizes.small,
                              border: `1px solid ${COLORS.border.default}`,
                              borderRadius: RADIUS,
                              padding: `${SPACING[4]} ${SPACING[8]}`,
                              color: COLORS.text.primary,
                              backgroundColor: COLORS.bg.base,
                              cursor: 'pointer',
                              outline: 'none'
                            }}
                          >
                            <option value="">No Project</option>
                            {projects.map(p => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => setEditingProject(null)}
                            style={{
                              fontFamily: TYPOGRAPHY.body.fontFamily,
                              fontSize: TYPOGRAPHY.body.sizes.tiny,
                              color: COLORS.text.secondary,
                              backgroundColor: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              padding: SPACING[4]
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.color = COLORS.text.primary)}
                            onMouseLeave={(e) => (e.currentTarget.style.color = COLORS.text.secondary)}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setEditingProject({type: 'meeting', id: meeting.id})}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: SPACING[4],
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            fontSize: TYPOGRAPHY.body.sizes.small,
                            color: COLORS.text.secondary,
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.color = COLORS.text.primary)}
                          onMouseLeave={(e) => (e.currentTarget.style.color = COLORS.text.secondary)}
                        >
                          <FolderIcon style={{ width: '16px', height: '16px' }} />
                          {meeting.project ? meeting.project.name : 'Assign to Project'}
                        </button>
                      )}
                    </div>

                    {/* Linked Recording */}
                    {linkedRecording ? (
                      <div style={{
                        backgroundColor: 'rgba(16, 185, 129, 0.1)',
                        border: `1px solid ${COLORS.status.success}`,
                        borderRadius: RADIUS,
                        padding: SPACING[12]
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                            <PlayIcon style={{ height: '20px', width: '20px', color: COLORS.status.success }} />
                            <div>
                              <p style={{
                                fontFamily: TYPOGRAPHY.body.fontFamily,
                                fontSize: TYPOGRAPHY.body.sizes.small,
                                fontWeight: '500',
                                color: COLORS.status.success,
                                margin: 0
                              }}>
                                Linked to Blue Dot Recording
                              </p>
                              <p style={{
                                fontFamily: TYPOGRAPHY.body.fontFamily,
                                fontSize: TYPOGRAPHY.body.sizes.tiny,
                                color: COLORS.status.success,
                                margin: 0
                              }}>
                                {linkedRecording.has_summary && 'Has summary'}
                                {linkedRecording.has_summary && linkedRecording.has_transcript && ' • '}
                                {linkedRecording.has_transcript && 'Has transcript'}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              setSelectedTab('recordings');
                              toggleExpanded('recording', linkedRecording.id);
                            }}
                            style={{
                              fontFamily: TYPOGRAPHY.body.fontFamily,
                              fontSize: TYPOGRAPHY.body.sizes.small,
                              color: COLORS.status.success,
                              fontWeight: '500',
                              backgroundColor: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              padding: SPACING[4]
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                            onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                          >
                            View Recording →
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        {linkingRecording === meeting.id ? (
                          <div style={{
                            backgroundColor: 'rgba(59, 130, 246, 0.1)',
                            border: `1px solid ${COLORS.accent.default}`,
                            borderRadius: RADIUS,
                            padding: SPACING[12]
                          }}>
                            <p style={{
                              fontFamily: TYPOGRAPHY.body.fontFamily,
                              fontSize: TYPOGRAPHY.body.sizes.small,
                              fontWeight: '500',
                              color: COLORS.accent.default,
                              margin: `0 0 ${SPACING[8]} 0`
                            }}>Select a recording to link:</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[8] }}>
                              {recordings.filter(r => !r.calendar_event).map(recording => (
                                <button
                                  key={recording.id}
                                  onClick={() => linkRecordingToMeeting(recording.id, meeting.id)}
                                  style={{
                                    width: '100%',
                                    textAlign: 'left',
                                    padding: `${SPACING[8]} ${SPACING[12]}`,
                                    backgroundColor: COLORS.bg.panel,
                                    border: `1px solid ${COLORS.accent.default}`,
                                    borderRadius: RADIUS,
                                    fontFamily: TYPOGRAPHY.body.fontFamily,
                                    fontSize: TYPOGRAPHY.body.sizes.small,
                                    color: COLORS.text.primary,
                                    cursor: 'pointer',
                                    transition: 'background-color 0.1s ease'
                                  }}
                                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = COLORS.bg.hover)}
                                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = COLORS.bg.panel)}
                                >
                                  {recording.title || recording.video_id} - {formatDate(recording.created_at)}
                                </button>
                              ))}
                              <button
                                onClick={() => setLinkingRecording(null)}
                                style={{
                                  width: '100%',
                                  textAlign: 'center',
                                  padding: `${SPACING[8]} ${SPACING[12]}`,
                                  backgroundColor: COLORS.bg.base,
                                  border: 'none',
                                  borderRadius: RADIUS,
                                  fontFamily: TYPOGRAPHY.body.fontFamily,
                                  fontSize: TYPOGRAPHY.body.sizes.small,
                                  color: COLORS.text.primary,
                                  cursor: 'pointer',
                                  transition: 'background-color 0.1s ease'
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = COLORS.bg.hover)}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = COLORS.bg.base)}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => setLinkingRecording(meeting.id)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: SPACING[4],
                              fontFamily: TYPOGRAPHY.body.fontFamily,
                              fontSize: TYPOGRAPHY.body.sizes.small,
                              color: COLORS.accent.default,
                              fontWeight: '500',
                              backgroundColor: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              padding: 0
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.color = COLORS.accent.hover)}
                            onMouseLeave={(e) => (e.currentTarget.style.color = COLORS.accent.default)}
                          >
                            <LinkIcon style={{ width: '16px', height: '16px' }} />
                            Link Blue Dot Recording
                          </button>
                        )}
                      </div>
                    )}

                    {/* Description */}
                    {meeting.description && (
                      <div>
                        <button
                          onClick={() => toggleExpanded('meeting', meeting.id)}
                          style={{
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            fontSize: TYPOGRAPHY.body.sizes.small,
                            fontWeight: '500',
                            color: COLORS.accent.default,
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.color = COLORS.accent.hover)}
                          onMouseLeave={(e) => (e.currentTarget.style.color = COLORS.accent.default)}
                        >
                          {isExpanded('meeting', meeting.id) ? 'Hide' : 'Show'} Description
                        </button>
                        {isExpanded('meeting', meeting.id) && (
                          <p style={{
                            marginTop: SPACING[8],
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            fontSize: TYPOGRAPHY.body.sizes.small,
                            color: COLORS.text.secondary,
                            whiteSpace: 'pre-wrap',
                            margin: `${SPACING[8]} 0 0 0`
                          }}>
                            {meeting.description}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </Panel>
              );
            })
          )}
        </div>
      )}

      {/* Recordings Tab */}
      {selectedTab === 'recordings' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
          {filteredRecordings.length === 0 ? (
            <Panel>
              <div style={{ padding: SPACING[48], textAlign: 'center' }}>
                <PlayIcon style={{
                  width: '48px',
                  height: '48px',
                  color: COLORS.text.tertiary,
                  margin: '0 auto',
                  marginBottom: SPACING[16]
                }} />
                <p style={{
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.headings.sizes.h3,
                  color: COLORS.text.secondary,
                  margin: 0,
                  marginBottom: SPACING[8]
                }}>No Blue Dot recordings found</p>
                <p style={{
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  color: COLORS.text.tertiary,
                  margin: 0
                }}>
                  {searchQuery ? 'Try adjusting your search criteria' : 'Recordings will appear here automatically via webhook'}
                </p>
              </div>
            </Panel>
          ) : (
            filteredRecordings.map((recording) => (
              <Panel key={recording.id}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <h3 style={{
                      fontFamily: TYPOGRAPHY.headings.fontFamily,
                      fontSize: TYPOGRAPHY.headings.sizes.h3,
                      fontWeight: TYPOGRAPHY.headings.weight,
                      color: COLORS.text.primary,
                      margin: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: SPACING[8]
                    }}>
                      <PlayIcon style={{ width: '20px', height: '20px', color: COLORS.status.success }} />
                      {recording.title || 'Blue Dot Recording'}
                      <Badge color="success">Blue Dot</Badge>
                    </h3>
                    <button
                      onClick={() => deleteRecording(recording.id)}
                      title="Delete recording"
                      style={{
                        color: COLORS.status.error,
                        padding: SPACING[8],
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        borderRadius: RADIUS,
                        transition: 'background-color 0.1s ease'
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      <TrashIcon style={{ width: '20px', height: '20px' }} />
                    </button>
                  </div>

                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: SPACING[16],
                    fontFamily: TYPOGRAPHY.body.fontFamily,
                    fontSize: TYPOGRAPHY.body.sizes.small,
                    color: COLORS.text.secondary
                  }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: SPACING[4] }}>
                      <ClockIcon style={{ width: '16px', height: '16px' }} />
                      {formatDate(recording.created_at)}
                    </span>
                    {recording.duration && (
                      <span>Duration: {formatDuration(recording.duration)}</span>
                    )}
                    <span>
                      {recording.has_summary && 'Has summary'}
                      {recording.has_summary && recording.has_transcript && ' • '}
                      {recording.has_transcript && 'Has transcript'}
                      {' • '}
                      {recording.is_complete ? 'Complete' : 'Partial'}
                    </span>
                  </div>

                  {/* Assignments */}
                  <div style={{
                    backgroundColor: COLORS.bg.base,
                    padding: SPACING[12],
                    borderRadius: RADIUS
                  }}>
                    <div style={{ marginBottom: SPACING[8] }}>
                      <span style={{
                        fontFamily: TYPOGRAPHY.body.fontFamily,
                        fontSize: TYPOGRAPHY.body.sizes.small,
                        fontWeight: '500',
                        color: COLORS.text.primary
                      }}>Assignments</span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[12] }}>
                      {/* Project */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontFamily: TYPOGRAPHY.body.fontFamily,
                          fontSize: TYPOGRAPHY.body.sizes.tiny,
                          fontWeight: '500',
                          color: COLORS.text.secondary,
                          marginBottom: SPACING[4]
                        }}>Project</label>
                        <select
                          value={recording.project?.id || ''}
                          onChange={(e) => updateProject('recording', recording.id, e.target.value ? Number(e.target.value) : null)}
                          style={{
                            width: '100%',
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            fontSize: TYPOGRAPHY.body.sizes.small,
                            border: `1px solid ${COLORS.border.default}`,
                            borderRadius: RADIUS,
                            padding: `${SPACING[4]} ${SPACING[8]}`,
                            color: COLORS.text.primary,
                            backgroundColor: COLORS.bg.panel,
                            cursor: 'pointer',
                            outline: 'none'
                          }}
                        >
                          <option value="">No Project</option>
                          {projects.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                      </div>

                      {/* Business (only if no project) */}
                      {!recording.project && (
                        <div>
                          <label style={{
                            display: 'block',
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            fontSize: TYPOGRAPHY.body.sizes.tiny,
                            fontWeight: '500',
                            color: COLORS.text.secondary,
                            marginBottom: SPACING[4]
                          }}>Business</label>
                          <select
                            value={recording.business?.id || ''}
                            onChange={(e) => updateRecordingAssignments(recording.id, { business_write: e.target.value ? Number(e.target.value) : null })}
                            style={{
                              width: '100%',
                              fontFamily: TYPOGRAPHY.body.fontFamily,
                              fontSize: TYPOGRAPHY.body.sizes.small,
                              border: `1px solid ${COLORS.border.default}`,
                              borderRadius: RADIUS,
                              padding: `${SPACING[4]} ${SPACING[8]}`,
                              color: COLORS.text.primary,
                              backgroundColor: COLORS.bg.panel,
                              cursor: 'pointer',
                              outline: 'none'
                            }}
                          >
                            <option value="">No Business</option>
                            {businesses.map(b => (
                              <option key={b.id} value={b.id}>{b.name}</option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Categories */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontFamily: TYPOGRAPHY.body.fontFamily,
                          fontSize: TYPOGRAPHY.body.sizes.tiny,
                          fontWeight: '500',
                          color: COLORS.text.secondary,
                          marginBottom: SPACING[4]
                        }}>Categories</label>
                        <div style={{
                          maxHeight: '128px',
                          overflowY: 'auto',
                          border: `1px solid ${COLORS.border.default}`,
                          borderRadius: RADIUS,
                          padding: SPACING[8],
                          display: 'flex',
                          flexDirection: 'column',
                          gap: SPACING[4],
                          backgroundColor: COLORS.bg.panel
                        }}>
                          {categories.map(c => (
                            <label key={c.id} style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: SPACING[8],
                              padding: `${SPACING[4]} ${SPACING[4]}`,
                              borderRadius: RADIUS,
                              cursor: 'pointer',
                              transition: 'background-color 0.1s ease'
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = COLORS.bg.hover)}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                            >
                              <input
                                type="checkbox"
                                checked={recording.categories?.some(cat => cat.id === c.id) || false}
                                onChange={(e) => {
                                  const currentIds = recording.categories?.map(cat => cat.id) || [];
                                  const newIds = e.target.checked
                                    ? [...currentIds, c.id]
                                    : currentIds.filter(id => id !== c.id);
                                  updateRecordingAssignments(recording.id, { category_ids: newIds });
                                }}
                                style={{
                                  cursor: 'pointer',
                                  accentColor: COLORS.accent.default
                                }}
                              />
                              <span style={{
                                fontFamily: TYPOGRAPHY.body.fontFamily,
                                fontSize: TYPOGRAPHY.body.sizes.small,
                                color: COLORS.text.primary
                              }}>{c.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Attendees */}
                      <div>
                        <label style={{
                          display: 'block',
                          fontFamily: TYPOGRAPHY.body.fontFamily,
                          fontSize: TYPOGRAPHY.body.sizes.tiny,
                          fontWeight: '500',
                          color: COLORS.text.secondary,
                          marginBottom: SPACING[4]
                        }}>Attendee People</label>
                        <div style={{
                          maxHeight: '128px',
                          overflowY: 'auto',
                          border: `1px solid ${COLORS.border.default}`,
                          borderRadius: RADIUS,
                          padding: SPACING[8],
                          display: 'flex',
                          flexDirection: 'column',
                          gap: SPACING[4],
                          backgroundColor: COLORS.bg.panel
                        }}>
                          {people.map(p => (
                            <label key={p.id} style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: SPACING[8],
                              padding: `${SPACING[4]} ${SPACING[4]}`,
                              borderRadius: RADIUS,
                              cursor: 'pointer',
                              transition: 'background-color 0.1s ease'
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = COLORS.bg.hover)}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                            >
                              <input
                                type="checkbox"
                                checked={recording.attendee_people?.some(ap => ap.id === p.id) || false}
                                onChange={() => togglePersonAttendee(recording.id, p.id, recording.attendee_people || [])}
                                style={{
                                  cursor: 'pointer',
                                  accentColor: COLORS.accent.default
                                }}
                              />
                              <span style={{
                                fontFamily: TYPOGRAPHY.body.fontFamily,
                                fontSize: TYPOGRAPHY.body.sizes.small,
                                color: COLORS.text.primary
                              }}>{p.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Meeting Link */}
                  {recording.calendar_event ? (
                    <div style={{
                      backgroundColor: 'rgba(59, 130, 246, 0.1)',
                      border: `1px solid ${COLORS.accent.default}`,
                      borderRadius: RADIUS,
                      padding: SPACING[12]
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                        <CalendarIcon style={{ height: '20px', width: '20px', color: COLORS.accent.default }} />
                        <p style={{
                          fontFamily: TYPOGRAPHY.body.fontFamily,
                          fontSize: TYPOGRAPHY.body.sizes.small,
                          color: COLORS.accent.default,
                          margin: 0
                        }}>
                          Linked to: <span style={{ fontWeight: '500' }}>{recording.calendar_event.title}</span>
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: SPACING[8] }}>
                      <button
                        onClick={() => createMeetingFromRecording(recording)}
                        style={{
                          padding: `${SPACING[4]} ${SPACING[12]}`,
                          fontFamily: TYPOGRAPHY.body.fontFamily,
                          fontSize: TYPOGRAPHY.body.sizes.tiny,
                          fontWeight: '500',
                          color: COLORS.bg.panel,
                          backgroundColor: COLORS.status.success,
                          border: 'none',
                          borderRadius: RADIUS,
                          cursor: 'pointer',
                          transition: 'opacity 0.1s ease'
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                        onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                      >
                        Create Calendar Meeting
                      </button>
                    </div>
                  )}

                  {/* Attendees */}
                  {recording.attendees.length > 0 && (
                    <div>
                      <span style={{
                        fontFamily: TYPOGRAPHY.body.fontFamily,
                        fontSize: TYPOGRAPHY.body.sizes.small,
                        fontWeight: '500',
                        color: COLORS.text.primary
                      }}>Attendees:</span>
                      <div style={{
                        marginTop: SPACING[8],
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: SPACING[8]
                      }}>
                        {recording.attendees.map((email, index) => (
                          <Badge key={index} color="success">
                            {email}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Expandable Content */}
                  {(recording.summary || recording.summary_v2 || recording.transcript_text || recording.transcript) && (
                    <div>
                      <button
                        onClick={() => toggleExpanded('recording', recording.id)}
                        style={{
                          fontFamily: TYPOGRAPHY.body.fontFamily,
                          fontSize: TYPOGRAPHY.body.sizes.small,
                          fontWeight: '500',
                          color: COLORS.status.success,
                          backgroundColor: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 0
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.8')}
                        onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                      >
                        {isExpanded('recording', recording.id) ? 'Hide' : 'Show'} Content
                      </button>

                      {isExpanded('recording', recording.id) && (
                        <div style={{
                          marginTop: SPACING[16],
                          display: 'flex',
                          flexDirection: 'column',
                          gap: SPACING[16],
                          borderTop: `1px solid ${COLORS.border.default}`,
                          paddingTop: SPACING[16]
                        }}>
                          {recording.summary_v2 && (
                            <div>
                              <h4 style={{
                                fontFamily: TYPOGRAPHY.body.fontFamily,
                                fontSize: TYPOGRAPHY.body.sizes.small,
                                fontWeight: '500',
                                color: COLORS.text.primary,
                                margin: `0 0 ${SPACING[8]} 0`
                              }}>Enhanced Summary</h4>
                              <p style={{
                                fontFamily: TYPOGRAPHY.body.fontFamily,
                                fontSize: TYPOGRAPHY.body.sizes.small,
                                color: COLORS.text.secondary,
                                whiteSpace: 'pre-wrap',
                                margin: 0
                              }}>{recording.summary_v2}</p>
                            </div>
                          )}
                          {recording.summary && !recording.summary_v2 && (
                            <div>
                              <h4 style={{
                                fontFamily: TYPOGRAPHY.body.fontFamily,
                                fontSize: TYPOGRAPHY.body.sizes.small,
                                fontWeight: '500',
                                color: COLORS.text.primary,
                                margin: `0 0 ${SPACING[8]} 0`
                              }}>Summary</h4>
                              <p style={{
                                fontFamily: TYPOGRAPHY.body.fontFamily,
                                fontSize: TYPOGRAPHY.body.sizes.small,
                                color: COLORS.text.secondary,
                                whiteSpace: 'pre-wrap',
                                margin: 0
                              }}>{recording.summary}</p>
                            </div>
                          )}
                          {(recording.transcript_text || recording.transcript) && (
                            <div>
                              <h4 style={{
                                fontFamily: TYPOGRAPHY.body.fontFamily,
                                fontSize: TYPOGRAPHY.body.sizes.small,
                                fontWeight: '500',
                                color: COLORS.text.primary,
                                margin: `0 0 ${SPACING[8]} 0`
                              }}>Transcript</h4>
                              <div style={{
                                backgroundColor: COLORS.bg.base,
                                borderRadius: RADIUS,
                                padding: SPACING[12],
                                maxHeight: '384px',
                                overflowY: 'auto'
                              }}>
                                {recording.transcript_text ? (
                                  <p style={{
                                    fontFamily: TYPOGRAPHY.body.fontFamily,
                                    fontSize: TYPOGRAPHY.body.sizes.small,
                                    color: COLORS.text.secondary,
                                    whiteSpace: 'pre-wrap',
                                    margin: 0
                                  }}>{recording.transcript_text}</p>
                                ) : recording.transcript ? (
                                  <div style={{
                                    fontFamily: TYPOGRAPHY.body.fontFamily,
                                    fontSize: TYPOGRAPHY.body.sizes.small,
                                    color: COLORS.text.secondary,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: SPACING[8]
                                  }}>
                                    {(() => {
                                      try {
                                        const parsed = typeof recording.transcript === 'string'
                                          ? JSON.parse(recording.transcript)
                                          : recording.transcript;

                                        if (Array.isArray(parsed)) {
                                          return parsed.map((entry: any, idx: number) => (
                                            <p key={idx} style={{ margin: `0 0 ${SPACING[8]} 0` }}>
                                              <span style={{ fontWeight: '600', color: COLORS.text.primary }}>{entry.speaker}:</span>{' '}
                                              {entry.text}
                                            </p>
                                          ));
                                        }
                                        return <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{JSON.stringify(parsed, null, 2)}</p>;
                                      } catch {
                                        return <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{recording.transcript}</p>;
                                      }
                                    })()}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Panel>
            ))
          )}
        </div>
      )}
      </div>
    </Screen>
  );
};

export default MeetingsPage;
